import gql from "graphql-tag";
import moment from "moment-timezone";
import _ from "lodash";
import { User } from "../../store/store";

import { Apollo } from "@/decorators";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { Company, Invoice, InvoiceProduct, InvoiceType } from "../../../../types";
import { createCompany, defaultLegalForm } from "../../services/companyServices";
import { mathFix } from "../../services/mathServices";

import CompanyTableComponent from "../admin/CompaniesTable.vue";
import EditCompany from "../EditCompany.vue";

import { ComponentBase } from "../ComponentBase";

import GET_COMPANY_INVOICE_DATA from "@/graphql/company/GetCompanyInvoiceData.gql";
import GET_COMPANY_LAST_DEPOSIT_VALIDATED from "@/graphql/company/GetCompanyLastDepositValidated.gql";
import GET_COMPANY_LAST_INVOICE_VALIDATED from "@/graphql/company/GetCompanyLastInvoiceValidated.gql";
import UPDATE_COMPANY_INVOICE_DATA from "@/graphql/company/UpdateCompanyInvoiceData.gql";
import CANCEL_AND_REPLACE_INVOICE from "../../graphql/invoice/CancelAndReplaceInvoice.gql";
import GENERATE_DEPOSITS from "../../graphql/invoice/GenerateDeposits.gql";
import INVOICE_QUERY from "../../graphql/invoice/Invoice.gql";
import LAST_INVOICE_DATE from "../../graphql/invoice/LastInvoiceDate.gql";

import axios from "axios";
import { InvoiceServices } from "../../../../backend/src/common/invoice/invoiceServices";
import getEnv from "../../env";
import { isInsideClosedAccountingPeriod } from "../../services/accountingPeriods";
import { getDBFormattedDate } from "../../services/dateServices";
import { vatRatesInvoice } from "../../services/vatServices";

import { confirmInvoice } from "@/types/confirmInvoice.types";

import defaultConfirmInvoiceScheme from "@/assets/json/confirmInvoiceDefaultScheme.json";
import { isNotSubjectToVAT } from "@/services/vat/vatHelpers.services";
import { Component, Prop, Watch } from "vue-facing-decorator";
import {
    CompanyInvoiceDTO,
    CompanyInvoiceDataResult,
    GetCompanyLastInvoiceValidatedResponse,
} from "@/types/invoices.types";

const env = getEnv();

const PaymentTypes = [
    { value: "WITH_ORDER", text: "À la commande" },
    { value: "DAYS_30", text: "30 jours" },
    { value: "DAYS_45", text: "45 jours" },
    { value: "DAYS_60", text: "60 jours" },
    { value: "DAYS_30_END_MONTH", text: "30 jours fin de mois" },
    { value: "DAYS_45_END_MONTH", text: "45 jours fin de mois" },
];

@Component({
    components: {
        companyTable: CompanyTableComponent,
        editCompany: EditCompany,
    },
    emits: ["create-product"],
})
export class InvoiceEditionBase extends ComponentBase {
    @Prop({ default: "" }) companyId: string;
    @Prop({ default: "" }) invoiceType: string;

    confirmInvoiceData: confirmInvoice = defaultConfirmInvoiceScheme;
    confirmInvoiceStep: number = 0;
    confirmInvoiceInitialStep: number = 0;

    @Apollo({
        query: GET_COMPANY_INVOICE_DATA,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return false;
        },
        specificResult({ data }: CompanyInvoiceDataResult) {
            if (data.company.invoiceData) {
                this.invoiceData = data.company.invoiceData;
            }
        },
    })
    invoiceData: CompanyInvoiceDTO;

    get activateTagguing() {
        return true;
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get moreThan2DecimalsAmount() {
        const result = this.invoice.lines.some((line) => {
            return line?.unitPrice?.toString().split(".")[1]?.length > 2;
        });
        return result;
    }

    mathFix(amount) {
        return mathFix(amount, this.maxDeciamalAmount).toString().replace(".", ",") + " €";
    }

    get maxDeciamalAmount() {
        let max = 0;
        this.invoice.lines.forEach((line) => {
            const decimals = line?.unitPrice?.toString().split(".")?.[1]?.length;
            if (decimals > max) {
                max = decimals;
            }
        });
        max = Math.max(max, 2);
        return max % 2 === 0 ? max : max + 1;
    }

    get isNewInvoice() {
        return (
            this.$route.name === "invoiceNew" ||
            this.$route.name === "invoiceMatrixNew" ||
            this.$route.name === "quotationNew" ||
            this.$route.name === "billDeliveryNew" ||
            this.$route.name === "invoiceRecurrenceNew"
        );
        // || this.invoiceType === "invoice"
        // || this.invoiceType === "quotation"
        // || this.invoiceType === "billDelivery"
    }

    get isInvoice() {
        if (this.invoice.type) return this.invoice.type === "INVOICE";
        return (
            this.$route.name === "invoiceNew" ||
            this.$route.name === "invoiceEdition" ||
            this.$route.name === "invoiceRecurrence" ||
            this.invoiceType === "invoice"
        );
    }

    get isInvoiceMatrix() {
        if (this.invoice.type) return this.invoice.type === "INVOICE_MATRIX";
        return this.$route.name === "invoiceMatrixNew";
    }

    get isQuote() {
        if (this.invoice.type) return this.invoice.type === "QUOTATION";
        return (
            this.$route.name === "quotationNew" ||
            this.$route.name === "quotationEdition" ||
            this.invoiceType === "quotation"
        );
    }

    get isBillOfDelivery() {
        if (this.invoice.type) return this.invoice.type === "BILL_DELIVERY";
        return (
            this.$route.name === "billDeliveryNew" ||
            this.$route.name === "billDeliveryEdition" ||
            this.invoiceType === "billDelivery"
        );
    }

    get isRecurrent() {
        if (this.invoice.type) return this.invoice.type === "INVOICE_RECURRENT";
        return (
            this.$route.name === "invoiceRecurrenceNew" ||
            this.$route.name === "invoiceRecurrenceEdition" ||
            this.invoiceType === "invoiceRecurrence"
        );
    }

    get isCreditNote() {
        if (this.invoice?.type) return this.invoice.type === "CREDIT_NOTE";
        return false;
    }

    get invoiceConfirmInvType() {
        return this.isDeposit ? "DEPOSIT" : this.invoice.type;
    }

    getInvoiceType(forceFormat = undefined): InvoiceType {
        if (forceFormat) {
            return forceFormat;
        }
        if (this.invoice?.type) {
            return this.invoice.type;
        }
        if (this.isQuote) {
            return "QUOTATION";
        } else if (this.isInvoice || this.isRecurrent || this.isDeposit) {
            return "INVOICE";
        } else if (this.isBillOfDelivery) {
            return "BILL_DELIVERY";
        } else if (this.isCreditNote) {
            return "CREDIT_NOTE";
        }
    }

    get isLocked() {
        if (this.invoice.type === "INVOICE_RECURRENT") return false;
        return (
            this.invoice.status === "BEING_VALIDATED" ||
            this.invoice.status === "VALIDATED" ||
            this.invoice.status === "ACCEPTED"
        );
    }

    get selectedCompany() {
        return this.$store.getters.getCompany(this.companyId);
    }

    get formatedDate() {
        return moment(this.invoice.date).format("DD/MM/YYYY");
    }

    get formatedDueDate() {
        return moment(this.invoice.dueDate).format("DD/MM/YYYY");
    }

    get dueDateTitle() {
        if (this.isInvoice || this.isInvoiceMatrix) {
            return "Date d'échéance";
        }
        if (this.isQuote) {
            return "Date de validité";
        }
        return "";
    }

    get displayAllVatRateTotals(): boolean {
        return true;
        return this.vatAmountTotals?.length > 1 ? true : false;
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, options);
    }

    formatDate(date) {
        return moment(date).format("DD/MM/YYYY");
    }

    dialogSelectCompany = false;

    dialogEditCompany = false;

    contactDialog = false;

    menu = false;

    menuDueDate = false;

    endReccurency = null;

    paymentTermsList = PaymentTypes;

    gonnaBeRecurrenceInvoice = false;

    isInfinitReccurency = false;

    errorDateDialog = false;

    userContact = {};

    selectVATType = false;

    convertDocumentDialog = false;

    dialogInvoiceDiscount = false;
    isValidDiscount = false;

    cancelAndReplaceDialog = false;

    areRulesValid = false;

    tags = [];

    // discountValue = 0.0
    globalDiscountType: "€" | "%" = "%";

    // deposits
    newDeposits: Array<{ name: string; date: Date; amount: number; percentage: number }>;
    depositSwitch = false;

    depositIsValid: boolean = false;

    // @Watch("depositIsValid")
    // watch_depositIsValid() {
    //     console.log("watch_depositIsValid", this.depositIsValid);
    // }

    //
    // income
    //
    defaultIncome = {
        id: "",
        type: "PRODUCT",
        title: "",
        description: "",
        reference: "",
        unitPrice: 0,
        vatRate: 20,
        quantity: 1,
        grossAmount: 0,
        netAmount: 0,
        vatAmount: 0,
        discountType: "%",
        discount: null,
        discountPercentage: null,
        product: null,
        expand: false,
        isOptional: false,
    };

    income = _.cloneDeep(this.defaultIncome);

    invoiceMatrixData = {
        products: [],
        matrix: [],
    };

    getNewIncome() {
        const newIncome = _.cloneDeep(this.defaultIncome);
        newIncome.id = "new-" + this.newLineId.toString();
        this.newLineId++;
        return newIncome;
    }

    resetIncome() {
        this.income = this.getNewIncome();
    }

    get formatDueDate() {
        return this.$t(
            "invoice.reccurency.dueDateFormat." + this.unitOfTime,
            {
                amount: this.dueDateAmount,
            },
            this.dueDateAmount,
        );
    }

    get incomeNetAmount() {
        return this.income.quantity * this.income.unitPrice;
    }

    setExistingProduct(product) {
        this.income.product = {
            id: product.id,
        };
        this.income.title = product.title;
        this.income.description = product.description;
        this.income.reference = product.reference;
        this.income.unitPrice = product.unitPrice;
        this.income.vatRate = product.vatRate;
        // this.income.accountableAccount = product.accountableAccount
        this.updateLineAmounts(this.income);
    }

    get addIncomeVerification() {
        return this.addIncomeVerification_();
    }

    addIncomeVerification_() {
        if (
            this.income &&
            this.income.title &&
            this.income.quantity > 0 &&
            this.income.grossAmount > 0
        ) {
            return false;
        }
        return true;
    }

    onDateChanged(date) {
        if (moment(this.invoice.dueDate).isBefore(moment(date))) {
            this.invoice.dueDate = date;
        }
    }

    //
    // status List
    //

    get statusList() {
        return [
            { value: "DRAFT", text: this.$t("invoice.DRAFT") },
            { value: "BEING_VALIDATED", text: this.$t("invoice.BEING_VALIDATED") },
            { value: "VALIDATED", text: this.$t("invoice.VALIDATED") },
            { value: "ACCEPTED", text: this.$t("invoice.ACCEPTED") },
            { value: "SIGNED", text: this.$t("invoice.SIGNED") },
            { value: "BEING_SIGNED", text: this.$t("invoice.BEING_SIGNED") },
        ];
    }

    selectedPaymentTerms = { value: "WITH_ORDER", text: "À la commande" };
    selectedStatus = { value: "DRAFT", text: "Brouillon" };

    selectedInvoiceLines = [];

    isSaving = false;

    dialogConfirmDuplicate = false;
    dialogConfirmInvoice = false;
    dialogDeleteDocument = false;
    deleteLoading = false;

    newLineId = 0;

    // vatList = [{ value: 20, text: '20%' }, { value: 10, text: '10%' }, { value: 8.5, text: '8.5%' }, { value: 5.5, text: '5.5%' }, { value: 2.1, text: '2.1%' }, { value: 0, text: '0%' }];
    // TAUX TVA Done

    taxSystems = {
        BIC_Normal: "BIC réel normal", // industrial and commercial profits
        BIC_Simplified: "BIC réel simplifié", // industrial and commercial profits
        BNC: "BNC", // bénéfice non commerciaux
        SCI: "SCI", // Société civile immobilière
        RF: "Revenus fonciers",
    };

    get headers() {
        const headers: { text: string; value: string; width?: string }[] = [
            { text: "Ref", value: "reference", width: "3%" },
            { text: "Produit", value: "title", width: !this.isBillOfDelivery ? "50%" : "90%" },
            { text: "Quantité", value: "quantity", width: "10%" },
        ];
        if (!this.isBillOfDelivery) {
            if (!isNotSubjectToVAT(this.selectedCompany.vatSystem)) {
                headers.push(
                    { text: "Prix unitaire", value: "unitPrice" },
                    { text: "Montant HT", value: "netAmount" },
                    { text: "Taux TVA", value: "vatRate" },
                );
            } else {
                headers.push(
                    { text: "Prix unitaire", value: "unitPrice" },
                    { text: "Montant", value: "netAmount" },
                );
            }
        }
        return headers;
    }

    get vatList() {
        return vatRatesInvoice.map((vat) => ({
            value: parseFloat(vat.value),
            text: vat.text,
        }));
    }

    get vatTypes() {
        return [
            { text: "Aucun", value: "NO_VAT_TYPE" },
            { text: "Intracom", value: "INTRACOM" }, // not vat accountable account (invoice)
            { text: "Export", value: "EXPORT" },
            { text: "Autoliquidation", value: "REVERSE_CHARGE" },
        ];
    }

    get unitsOfTime() {
        return [
            { value: "DAYS", text: this.$t("invoice.reccurency.unitOfTime.DAYS") },
            { value: "WEEKS", text: this.$t("invoice.reccurency.unitOfTime.WEEKS") },
            { value: "MONTHS", text: this.$t("invoice.reccurency.unitOfTime.MONTHS") },
        ];
    }

    get subjectToVAT() {
        return !isNotSubjectToVAT(this.selectedCompany.vatSystem);
    }

    // get dueDate() {
    //     console.log("due date", this.selectedPaymentTerms.value)
    //     const date = moment(this.invoice.date)
    //     switch (this.selectedPaymentTerms.value) {
    //     case 'DAYS_30': date.add(30, 'days'); break;
    //     case 'DAYS_45': date.add(45, 'days'); break;
    //     case 'DAYS_60': date.add(60, 'days'); break;
    //     case 'DAYS_30_END_MONTH': date.endOf("month").add(30, 'days'); break;
    //     case 'DAYS_45_END_MONTH': date.endOf("month").add(45, 'days'); break;
    //     }

    //     return date.format('YYYY-MM-DD')
    // }

    getSubTotals(index) {
        if (this.invoiceServices?.computation?.totals[index]) {
            return this.invoiceServices?.computation?.totals[index];
        }
        return {
            netAmount: 0,
            vatAmount: 0,
        };
    }

    @Watch("invoice.lines", { deep: true })
    updateSubTotals() {
        let subTotalIndex = 0;
        for (const line of this.invoice.lines) {
            if (line.type === "TOTAL") {
                const amount = this.getSubTotals(subTotalIndex);
                line.netAmount = amount.netAmount;
                line.vatAmount = amount.vatAmount;
                line.grossAmount = amount.netAmount + amount.vatAmount;
            }
            subTotalIndex += 1;
        }
    }

    /**
     * Make computation on lines. The original object is not modified
     */
    // get formattedLines() {
    //     const newLines = [];
    //     if (!this.invoice?.lines) {
    //         return this.invoice?.lines;
    //     }
    //     let subTotalIndex = 0;
    //     this.invoice.lines.forEach((line) => {
    //         const lineCopy = { ...line };
    //         if (line.type === "TOTAL") {
    //             const amount = this.getSubTotals(subTotalIndex);
    //             // console.log("amount", amount)
    //             lineCopy.netAmount = amount.netAmount;
    //             lineCopy.vatAmount = amount.vatAmount;
    //             lineCopy.grossAmount = amount.netAmount + amount.vatAmount;
    //         }
    //         newLines.push(lineCopy);
    //         subTotalIndex += 1;
    //     });
    //     // console.log("newLines", newLines)
    //     return newLines;
    // }

    get invoiceServices() {
        const lSign = this.invoice?.type !== "CREDIT_NOTE" ? 1 : -1;
        let vatType;
        const defaultSalesAccountableAccount = "700000";
        // let quotation
        return new InvoiceServices(
            this.invoice.lines,
            lSign,
            vatType,
            this.invoice.discount,
            this.invoice.discountPercentage,
            defaultSalesAccountableAccount,
            this.quotation,
        );
    }

    get netAmountTotal() {
        return -this.invoiceServices.netAmountTotal;
    }

    get vatAmountTotals() {
        return this.invoiceServices.vatAmountTotals;
    }

    get vatAmountTotal() {
        return -this.invoiceServices.vatAmountTotal;
    }

    get grossAmountTotal() {
        return -this.invoiceServices.grossAmountTotal;
    }

    defaultInvoice = {
        id: null,
        type: null,
        title: "",
        status: "DRAFT",
        number: "",
        date: moment().format("YYYY-MM-DD"),
        dueDate: moment().add("30 days").format("YYYY-MM-DD"),
        paymentTerms: undefined,
        lines: [],
        paymentDetails: "",
        legalText: "",
        paymentMode: "",
        vatType: null,
        periodicity: null,
        receivers: [
            {
                firstname: "",
                lastname: "",
                email: "",
                phoneNumber: "",
            },
        ],
        // receiver: {
        //     firstname: "",
        //     lastname: ""
        // },
        // receiverEmail: "",
        nextDate: null,
        startDate: null,
        endDate: null,
        dueDateAmount: null,
        dueDateUnitOfTime: null,
        client: {
            id: "",
            name: "",
            address: "",
            postalCode: "",
            city: "",
            country: "",
            siret: "",
            siren: "",
            vatNumber: "",
            isActive: true,
        },
        reccurenceInvoice: null,
        document: null,
        creditNoteOf: null,
        autoValidation: false,
        sendEmailAfterValidation: false,
        data: {
            paymentDetails: "",
            legalText: "",
            paymentMode: "",
            additionalComment: "",
            internalComment: "",
            receivers: [
                {
                    firstname: "",
                    lastname: "",
                    email: "",
                    phoneNumber: "",
                },
            ],
        },
        discount: null,
        discountPercentage: null,
    };

    @Apollo({
        query: INVOICE_QUERY,
        variables() {
            return {
                id: this.$route.params.id,
            };
        },
        skip() {
            return this.isNewInvoice || !this.$route.params.id;
        },
        specificResult({ data }) {
            if (data.invoice?.type === "INVOICE_RECURRENT") {
                // this.isRecurrent = true
                this.dueDateAmount = data.invoice.dueDateAmount;
                this.unitOfTime = data.invoice.dueDateUnitOfTime;
                data.invoice.startDate = moment(data.invoice.startDate)
                    .format("YYYY-MM-DD")
                    .toString();
                if (data.invoice.endDate) {
                    data.invoice.endDate = moment(data.invoice.endDate)
                        .format("YYYY-MM-DD")
                        .toString();
                    this.isInfinitReccurency = false;
                } else {
                    this.isInfinitReccurency = true;
                }
                data.invoice.nextDate = moment(data.invoice.nextDate)
                    .format("YYYY-MM-DD")
                    .toString();
                if (data.invoice.autoValidation === null) {
                    data.invoice.autoValidation = false;
                }
                if (data.invoice.sendEmailAfterValidation === null) {
                    data.invoice.sendEmailAfterValidation = false;
                }
            }

            // Set as Date objects for v-date-picker
            if (data.invoice.dueDate) {
                data.invoice.dueDate = moment(data.invoice.dueDate); //.toDate();
            }
            if (data.invoice.date) {
                data.invoice.date = moment(data.invoice.date); //.toDate();
            }

            const defaultData = {
                legalText: data.invoice.legalText || "",
                paymentDetails: data.invoice.paymentDetails || "",
                paymentMode: data.invoice.paymentMode || "",
                additionalComment: data.invoice.additionalComment || "",
                internalComment: data.invoice.internalComment || "",
                receivers: data.invoice.receivers || [],
            };
            data.invoice.data = { ...defaultData, ...data.invoice.data };
            if (!data.invoice.data?.receivers?.length) {
                data.invoice.data.receivers = [
                    {
                        firstname: "",
                        lastname: "",
                        email: "",
                        phoneNumber: "",
                    },
                ];
            }

            if (data.invoice.discount) {
                this.globalDiscountType = "€";
            }

            for (const l of data.invoice.lines) {
                if (l.description) {
                    l.expand = true;
                } else {
                    l.expand = false;
                }
                if (l.discount) {
                    l.discountType = "€";
                } else {
                    l.discountType = "%";
                }
                // if(this.displayTTCAmounts) {
                //     l.unitPrice = fromHT_UnitPriceToTTC(l)
                // }
                if (!l.isOptional) {
                    l.isOptional = false;
                }
                // this.updateLineAmounts(l);
            }

            if (data.invoice.data?.deposits?.length) {
                this.depositSwitch = true;
            }

            this.selectedPaymentTerms = this.paymentTermsList.find(
                (e) => e.value === data.invoice.paymentTerms,
            );
            this.invoice = data.invoice;
        },
    })
    invoice = _.cloneDeep(this.defaultInvoice);

    @Apollo({
        query: INVOICE_QUERY,
        variables() {
            return {
                id: this.invoice.depositOf.id,
            };
        },
        skip() {
            return !this.invoice?.depositOf?.id;
        },
        specificResult({ data }) {
            if (data.invoice && data.invoice.type === "INVOICE_RECURRENT") {
                data.invoice.startDate = moment(data.invoice.startDate)
                    .format("YYYY-MM-DD")
                    .toString();
                if (data.invoice.endDate) {
                    data.invoice.endDate = moment(data.invoice.endDate)
                        .format("YYYY-MM-DD")
                        .toString();
                    this.isInfinitReccurency = false;
                } else {
                    this.isInfinitReccurency = true;
                }
                data.invoice.nextDate = moment(data.invoice.nextDate)
                    .format("YYYY-MM-DD")
                    .toString();
                if (data.invoice.autoValidation === null) {
                    data.invoice.autoValidation = false;
                }
                if (data.invoice.sendEmailAfterValidation === null) {
                    data.invoice.sendEmailAfterValidation = false;
                }
            }
            // Set as Date objects for v-date-picker
            if (data.invoice.dueDate) {
                data.invoice.dueDate = moment(data.invoice.dueDate); //.toDate();
            }
            if (data.invoice.date) {
                data.invoice.date = moment(data.invoice.date); //.toDate();
            }
            if (!data.invoice.data?.receivers?.length) {
                data.invoice.receivers = [
                    {
                        firstname: "",
                        lastname: "",
                        email: "",
                        phoneNumber: "",
                    },
                ];
            }
            this.quotation = data.invoice;
        },
    })
    quotation = _.cloneDeep(this.defaultInvoice);

    refresh() {
        if (this.$apollo?.queries?.invoice?.refetch) {
            this.$apollo.queries.invoice.refetch();
        }
    }

    // @Watch('invoice', { deep: true })
    // initisRecurrent() {
    //     if (this.invoice.type === 'INVOICE_RECURRENT') {
    //         this.isRecurrent = true
    //     }
    // }

    dialogCreditNote = false;

    @Apollo(
        {
            query: gql`
                query ($id: ID!) {
                    company(where: { id: $id }) {
                        id
                        name
                        siret
                        siren
                        vatNumber
                        address
                        postalCode
                        city
                        country
                        taxSystem
                        legalForm
                        legalFormCategory
                        isActive
                        shareCapital
                        registrationTown
                        thirdPartyCompanyData
                        activities {
                            id
                            name
                        }
                        logo {
                            id
                            key
                            signedUrl
                        }
                    }
                }
            `,
            variables() {
                return {
                    id: this.selectedCompany.id,
                };
            },
        },
        "company",
    )
    userCompany: Company = { siret: "", siren: "", vatNumber: "" };

    @Watch("userCompany")
    onCompanyChanged() {
        if (
            this.userCompany &&
            this.isNewInvoice &&
            !this.$route.params.id &&
            !this.invoice.data.legalText
        ) {
            this.setDefaultLegalForm();
        }
    }

    @Apollo(
        {
            query: gql`
                query products($companyId: ID!) {
                    invoiceProducts(
                        where: {
                            company: { id: $companyId }
                            OR: [{ type: null }, { type: PRODUCT }]
                        }
                    ) {
                        id
                        reference
                        title
                        unitPrice
                        vatRate
                    }
                }
            `,
            variables() {
                return {
                    companyId: this.selectedCompany.id,
                    stock: true,
                };
            },
            skip() {
                return !this.selectedCompany || !this.selectedCompany.id;
            },
        },
        (data) => (data ? data.invoiceProducts : []),
    )
    products: InvoiceProduct[] = [];

    @Watch("invoice.status")
    invoiceStatusChanged() {
        this.selectedStatus = this.statusList.find((e) => e.value === this.invoice.status);
    }

    _reset() {
        this.invoice = _.cloneDeep(this.defaultInvoice);
    }

    resetClient() {
        this.invoice.client = {
            id: "",
            name: "",
            address: "",
            postalCode: "",
            city: "",
            country: "",
            siret: "",
            siren: "",
            vatNumber: "",
            isActive: true,
        };
    }

    async onMounted() {
        console.log("router:", this.$router);
        if (this.isNewInvoice) {
            const result = await this.$apollo.query({
                query: gql`
                    query invoiceSettings($companyId: ID!) {
                        company(where: { id: $companyId }) {
                            id
                            invoiceTextPaymentDetails
                            invoiceTextLegalNotices
                            invoiceTextPaymentMode
                        }
                    }
                `,
                variables: {
                    companyId: this.selectedCompany.id,
                },
            });
            if (result && result.data && result.data.company) {
                this.invoice.data.paymentDetails = result.data.company.invoiceTextPaymentDetails;
                this.invoice.data.legalText = result.data.company.invoiceTextLegalNotices;
                this.invoice.data.paymentMode = result.data.company.invoiceTextPaymentMode;
            }
        }
        this.addListener("invoice");
    }

    beforeDestroy() {
        this.removeListener();
    }

    sumUpReccurency() {
        let nbrOfInvoices = 0;
        let startReccurency = moment(this.invoice.startDate);
        const endReccurency = moment(this.invoice.endDate);
        if (this.invoice.startDate && this.invoice.endDate && this.invoice.periodicity) {
            do {
                nbrOfInvoices++;
                switch (this.invoice.periodicity) {
                    case "ANNUAL": {
                        startReccurency.add(1, "y");
                        break;
                    }
                    case "BIANNUAL": {
                        startReccurency.add(6, "M");
                        break;
                    }
                    case "QUARTERLY": {
                        startReccurency.add(3, "M");
                        break;
                    }
                    case "MONTHLY": {
                        startReccurency.add(1, "M");
                        break;
                    }
                    case "BIMONTHLY": {
                        if (nbrOfInvoices % 2 === 0) {
                            startReccurency = moment(this.invoice.startDate).add(
                                (1 / 2) * nbrOfInvoices,
                                "M",
                            );
                        } else {
                            startReccurency = moment(this.invoice.startDate)
                                .add((1 / 2) * (nbrOfInvoices - 1), "M")
                                .add(15, "d");
                        }
                        // startReccurency.add(0.5, "M");  // doesn't work because of moment rounded
                        break;
                    }
                    default:
                        console.error("sumUpReccurency Error");
                        break;
                }
            } while (startReccurency < endReccurency);
        }
        return this.$t("invoice.reccurency.resume", { nbrInvoices: nbrOfInvoices }, nbrOfInvoices);
    }

    onbalanceDateChanged(data) {
        this.invoice.startDate = data;
    }

    get isAvailablePriceForCreditNote() {
        if (this.invoice?.type === "CREDIT_NOTE" && this.invoice.creditNoteOf) {
            const invoiceAmount = this.invoice.creditNoteOf.lines.reduce(
                (acc, l) => acc + l.grossAmount,
                0,
            );
            if (this.netAmountTotal > invoiceAmount) {
                return false;
            }
            return true;
        }
        return true;
    }

    async companySelected(obj: Company) {
        console.log(obj);
        if (obj.id) {
            this.invoice.client = obj as Required<Company>;
            // this.dialogSelectCompany = false;
        } else {
            obj.id = await createCompany(this.$apollo, obj);
            this.invoice.client = obj as Required<Company>;
            // this.dialogSelectCompany = false;
        }
    }

    updateLineAmounts(line) {
        if (this.displayTTCAmounts) {
            line.grossAmount = line.quantity * line.unitPrice;
            if (line.vatRate) {
                line.vatAmount = (line.grossAmount * line.vatRate) / (100 + line.vatRate);
            } else {
                line.vatAmount = 0;
            }
            line.netAmount = line.grossAmount - line.vatAmount;
        } else {
            line.netAmount = line.quantity * line.unitPrice;
            if (line.vatRate) {
                line.vatAmount = (line.netAmount * line.vatRate) / 100;
            } else {
                line.vatAmount = 0;
            }
            line.grossAmount = line.netAmount + line.vatAmount;
        }
    }

    closeDialogEditCompany() {
        this.dialogEditCompany = false;
    }

    addEmptyLine() {
        this.invoice.lines.push(this.getNewIncome());
    }

    createProduct() {
        if (this.$refs.createInvoiceProduct) {
            this.$refs.createInvoiceProduct.createProduct();
        }
    }

    _addLine(income) {
        const quantity = income.quantity || income.unitQuantity || 1;
        const invoiceLine = _.cloneDeep(this.defaultIncome);
        invoiceLine.product = income?.product || income;
        invoiceLine.reference = income.reference;
        invoiceLine.title = income.title;
        invoiceLine.description = income.description || "";
        invoiceLine.quantity = quantity;
        invoiceLine.unitPrice = income.unitPrice;
        invoiceLine.vatRate = income.vatRate;
        invoiceLine.expand = income.description ? true : false;

        this.updateLineAmounts(invoiceLine);
        if (income?.accountableAccount?.name.startsWith("709")) {
            if (income?.product?.percentage || income?.percentage) {
                this.invoice.lines.push(invoiceLine);
            } else {
                const firstDiscountWithPercentageIndex = this.invoice.lines.findIndex(
                    (l) =>
                        l.product?.accountableAccount?.name.startsWith("709") &&
                        l.product?.percentage,
                );
                if (firstDiscountWithPercentageIndex === -1) {
                    this.invoice.lines.push(invoiceLine);
                } else {
                    this.invoice.lines.splice(firstDiscountWithPercentageIndex, 0, invoiceLine);
                }
            }
        } else {
            const firstDiscountIndex = this.invoice.lines.findIndex((l) =>
                l.product?.accountableAccount?.name.startsWith("709"),
            );
            if (firstDiscountIndex === -1) {
                this.invoice.lines.push(invoiceLine);
            } else {
                this.invoice.lines.splice(firstDiscountIndex, 0, invoiceLine);
            }
        }
        this.resetIncome();
    }

    goToMainPageInvoiceDiscount() {
        this.$refs.invoiceDiscount?.goToMainPage();
    }

    addLine(product) {
        this._addLine(product);
    }

    linesToDelete = [];

    removeInvoiceLine(index) {
        if (this.invoice.lines[index].id) {
            this.linesToDelete.push({ id: this.invoice.lines[index].id });
        }
        this.invoice.lines = this.invoice.lines.filter((e, i) => i !== index);
    }

    async print() {
        this.downloadLoading = true;
        console.log("this.invoice", this.invoice);
        if (
            this.invoice.id &&
            !["VALIDATED", "BEING_SIGNED", "SIGNED"].includes(this.invoice.status)
        ) {
            await this.saveInvoice(false);
        }

        if (this.invoice?.signedUrl) {
            console.log(this.invoice.signedUrl);
            window.open(this.invoice.signedUrl, "_blank");
            // }
            this.downloadLoading = false;
            return;
        }
        axios.get(
            `${env.VUE_APP_BACKEND}/document/print/invoice/${
                this.invoice.id
            }/pdf?dt="${new Date().getTime()}`,
            {},
        );
    }

    reccurentInvoiceID = null;

    async acceptInvoice() {
        const newInvoice = !this.invoice.id;
        await this._saveInvoice();

        this.isSaving = true;

        await this.$apollo.mutate({
            mutation: gql`
                mutation updateInvoice($invoiceId: ID!) {
                    updateInvoice(where: { id: $invoiceId }, data: { status: ACCEPTED }) {
                        id
                        status
                    }
                }
            `,
            variables: {
                invoiceId: this.invoice.id,
            },
        });

        this.isSaving = false;

        if (newInvoice) {
            this.updatePath();
        }
    }

    incomeIsDiscount(income) {
        if (income?.product?.accountableAccount?.name.startsWith("709")) {
            return true;
        }
        return false;
    }

    regenerateInvoiceDocumentLoading: boolean = false;

    async regenerateInvoiceDocument() {
        if (this.invoice.status === "VALIDATED") {
            this.regenerateInvoiceDocumentLoading = true;
            await this.$apollo.mutate({
                mutation: gql`
                    mutation ReGenerateInvoiceDocument($invoiceId: ID!) {
                        reGenerateInvoiceDocument(where: { id: $invoiceId }) {
                            id
                        }
                    }
                `,
                variables: {
                    invoiceId: this.invoice.id,
                },
            });
            this.regenerateInvoiceDocumentLoading = false;
            this.refresh();
        }
    }

    setNewInvoiceData() {
        let baseFieldName = "";

        switch (this.invoice.type) {
            case "QUOTATION":
                baseFieldName = "quote";
                break;
            case "CREDIT_NOTE":
                baseFieldName = "creditNote";
                break;
            case "BILL_DELIVERY":
                baseFieldName = "deliveryBill";
                break;
            case "INVOICE_RECURRENT":
            case "INVOICE":
                if (this.isDeposit) {
                    baseFieldName = "deposit";
                } else {
                    baseFieldName = "invoice";
                }
                break;
        }
        if (!this.invoiceData) {
            this.invoiceData = {};
        }
        if (!this.invoiceData.chrono) {
            this.invoiceData.chrono = {};
        }
        if (!this.invoiceData.counter) {
            this.invoiceData.counter = {};
        }
        this.invoiceData.chrono[baseFieldName + "Chrono"] = this.confirmInvoiceData.chrono;
        this.invoiceData.chrono[baseFieldName + "ResetCounterCondition"] =
            this.confirmInvoiceData.resetChronoCondition;
        const date = new Date(this.invoice.date);
        const year = date.getFullYear().toString();
        const month = date.getMonth().toString();
        switch (this.confirmInvoiceData.resetChronoCondition) {
            case "NONE":
                this.invoiceData.counter[baseFieldName] = {};
                this.invoiceData.counter[baseFieldName].NONE = this.confirmInvoiceData.counter;
                break;
            case "MONTH":
                if (!this.invoiceData.counter[baseFieldName]) {
                    this.invoiceData.counter[baseFieldName] = {};
                    this.invoiceData.counter[baseFieldName][year] = {};
                    this.invoiceData.counter[baseFieldName][year][month] =
                        this.confirmInvoiceData.counter;
                } else if (!this.invoiceData.counter[baseFieldName][year]) {
                    this.invoiceData.counter[baseFieldName][year] = {};
                    this.invoiceData.counter[baseFieldName][year][month] =
                        this.confirmInvoiceData.counter;
                } else {
                    this.invoiceData.counter[baseFieldName][year][month] =
                        this.confirmInvoiceData.counter;
                }
                break;
            case "YEAR":
                if (!this.invoiceData.counter[baseFieldName]) {
                    this.invoiceData.counter[baseFieldName] = {};
                    this.invoiceData.counter[baseFieldName][year] = this.confirmInvoiceData.counter;
                } else {
                    this.invoiceData.counter[baseFieldName][year] = this.confirmInvoiceData.counter;
                }
                break;
        }
        return this.invoiceData;
    }

    async confirmInvoice() {
        this.isSaving = true;
        const newInvoice = !this.invoice.id;
        await this._saveInvoice();

        this.confirmInvoiceData.counter = Number(this.confirmInvoiceData.counter);
        if (this.confirmInvoiceInitialStep === 1) {
            const newInvoiceData = this.setNewInvoiceData();
            await this.$apollo.mutate({
                mutation: UPDATE_COMPANY_INVOICE_DATA,
                variables: {
                    companyId: this.companyId,
                    invoiceData: newInvoiceData,
                },
            });
        }
        if (this.confirmInvoiceInitialStep >= 4 || Number.isNaN(this.confirmInvoiceData.counter)) {
            this.confirmInvoiceData.counter = 0;
        }
        const result = await this.$apollo.mutate({
            mutation: gql`
                mutation ConfirmInvoice($invoiceId: ID!, $initialCounter: Int) {
                    confirmInvoice(where: { id: $invoiceId, initialCounter: $initialCounter }) {
                        document
                        number
                        status
                    }
                }
            `,
            variables: {
                invoiceId: this.invoice.id,
                initialCounter: this.confirmInvoiceData.counter,
            },
        });

        this.invoice.status = result.data.confirmInvoice.status;
        this.invoice.number = result.data.confirmInvoice.number;
        if (result.data.confirmInvoice.document) {
            this.isSaving = false;
        }

        if (this.invoice.type === "INVOICE_RECURRENT") {
            this.isSaving = false;
        }

        if (newInvoice) {
            this.updatePath();
        } else {
            this.refresh();
        }
    }

    unitOfTime = "DAYS";
    dueDateAmount = 1;

    async upsertInvoiceMatrix(duplicate) {
        // add Tags ?
        const invoiceId = !duplicate && this.invoice && this.invoice.id ? this.invoice.id : "";

        const data = {
            products: this.invoiceMatrixData.products.map((p) => p.id),
            matrix: [],
            legalText: this.invoice.data.legalText || "",
            paymentDetails: this.invoice.data.paymentDetails || "",
            paymentMode: this.invoice.data.paymentMode || "",
            additionalComment: this.invoice.data.additionalComment || "",
            internalComment: this.invoice.data.internalComment || "",
            receivers: { ...this.invoice.data.receivers },
        };
        for (const clientData of this.invoiceMatrixData.matrix) {
            data.matrix.push({
                selected: clientData.selected,
                clientId: clientData.client.id,
                quantities: clientData.quantities,
            });
        }

        const save_mutation = gql`
            mutation upsertInvoice(
                $invoiceId: ID
                $title: String!
                $company: ID!
                $owner: ID!
                $paymentTerms: InvoicePaymentTerms
                $date: DateTime
                $dueDate: DateTime
                $data: Json
                $vatType: VATType
                $autoValidation: Boolean
                $sendEmailAfterValidation: Boolean
            ) {
                upsertInvoice(
                    where: { id: $invoiceId }
                    create: {
                        title: $title
                        type: INVOICE_MATRIX
                        status: DRAFT
                        company: { connect: { id: $company } }
                        owner: { connect: { id: $owner } }
                        date: $date
                        dueDate: $dueDate
                        data: $data
                        paymentTerms: $paymentTerms
                        vatType: $vatType
                        autoValidation: $autoValidation
                        sendEmailAfterValidation: $sendEmailAfterValidation
                    }
                    update: {
                        title: $title
                        date: $date
                        dueDate: $dueDate
                        data: $data
                        paymentTerms: $paymentTerms
                        vatType: $vatType
                        autoValidation: $autoValidation
                        sendEmailAfterValidation: $sendEmailAfterValidation
                    }
                ) {
                    id
                }
            }
        `;
        const result = await this.$apollo.mutate({
            mutation: save_mutation,
            variables: {
                invoiceId: invoiceId,
                title: this.invoice.title || undefined,
                company: this.selectedCompany.id,
                owner: this.$store.state?.user ? this.$store.state.user.id : null,
                date: getDBFormattedDate(this.invoice.date),
                dueDate: getDBFormattedDate(this.invoice.dueDate),
                paymentTerms: undefined,
                vatType: this.invoice.vatType,
                autoValidation: this.invoice.autoValidation || false,
                sendEmailAfterValidation: this.invoice.sendEmailAfterValidation || false,
                data,
            },
        });
        this.isSaving = false;
        if (!invoiceId && result.data && result.data.upsertInvoice) {
            this.invoice.id = result.data.upsertInvoice.id;
        }
    }

    async upsertReccurentInvoice(duplicate, lines) {
        const lines_to_update = lines.filter(
            (l) => !duplicate && l.id && l.id.indexOf("new-") !== 0,
        );
        const lines_to_create = lines.filter(
            (l) => duplicate || !l.id || l.id.indexOf("new-") === 0,
        );

        const linesNoDelete = lines.map((e) => e.id).filter((e) => e !== null && e !== "");

        const linesToCreate = lines_to_create.map((l) => {
            let productId;
            if (l.product) {
                productId = l.product?.product?.id || l.product.id;
            } else {
                const linked_product = l.reference
                    ? this.products.find((e) => e.reference === l.reference)
                    : undefined;
                if (linked_product) {
                    productId = linked_product.id;
                }
            }
            if (l.discount && l.discountPercentage) {
                console.error(
                    "invoiceLine has discountError : lineTitle=" +
                        l.title +
                        ", invoiceId: " +
                        this.invoice.id,
                );
            }
            return {
                reference: l.reference,
                title: l.title,
                quantity: l.quantity,
                description: l.description,
                unitPrice: l.unitPrice,
                netAmount: l.netAmount,
                grossAmount: l.grossAmount,
                vatAmount: l.vatAmount,
                vatRate: l.vatRate,
                product: productId
                    ? {
                          connect: { id: productId },
                      }
                    : undefined,
                discount: l.discount,
                discountPercentage: l.discountPercentage,
            };
        });

        const linesToUpdate = lines_to_update.map((l) => {
            let productId;
            if (l.product) {
                productId = l.product.id;
            } else {
                const linked_product = l.reference
                    ? this.products.find((e) => e.reference === l.reference)
                    : undefined;
                if (linked_product) {
                    productId = linked_product.id;
                }
            }
            if (l.discount && l.discountPercentage) {
                console.error(
                    "invoiceLine has discountError : lineTitle=" +
                        l.title +
                        ", invoiceId: " +
                        this.invoice.id,
                );
            }
            return {
                where: { id: l.id },
                data: {
                    reference: l.reference,
                    title: l.title,
                    description: l.description,
                    quantity: l.quantity,
                    unitPrice: l.unitPrice,
                    netAmount: l.netAmount,
                    grossAmount: l.grossAmount,
                    vatAmount: l.vatAmount,
                    vatRate: l.vatRate,
                    product: productId
                        ? {
                              connect: { id: productId },
                          }
                        : undefined,
                    discount: l.discount,
                    discountPercentage: l.discountPercentage,
                },
            };
        });

        const invoiceId = !duplicate && this.invoice && this.invoice.id ? this.invoice.id : "";
        if (invoiceId) {
            if (linesNoDelete.length) {
                // Delete Many lines before create/update lines
                await this.$apollo.mutate({
                    mutation: gql`
                        mutation updateInvoice($invoiceId: ID!, $linesNoDelete: [ID!]!) {
                            updateInvoice(
                                where: { id: $invoiceId }
                                data: { lines: { deleteMany: { id_not_in: $linesNoDelete } } }
                            ) {
                                id
                            }
                        }
                    `,
                    variables: {
                        invoiceId: this.invoice.id,
                        linesNoDelete: linesNoDelete,
                    },
                });
            }
        }

        const save_mutation = gql`
            mutation upsertInvoice(
                $invoiceId: ID
                $title: String
                $company: ID!
                $createClient: CompanyCreateOneInput
                $updateClient: CompanyUpdateOneInput
                $createOwner: UserCreateOneInput
                $periodicity: InvoicePeriodicity
                $paymentTerms: InvoicePaymentTerms
                $unitOfTime: InvoiceDueDateUnitOfTime!
                $dueDateAmount: Int
                $endDate: DateTime
                $startDate: DateTime
                $discount: Float
                $discountPercentage: Float
                $linesToCreate: [InvoiceLineCreateInput!]!
                $linesToUpdate: [InvoiceLineUpdateWithWhereUniqueNestedInput!]!
                # $tagsToCreate: [TagCreateInput!],
                # $tagsToUpdate: [TagUpdateWithWhereUniqueNestedInput!]
                $vatType: VATType
                $autoValidation: Boolean
                $sendEmailAfterValidation: Boolean
                $netAmount: Float
                $grossAmount: Float
                $vatAmount: Float
                $data: Json
            ) {
                upsertInvoice(
                    where: { id: $invoiceId }
                    create: {
                        title: $title
                        type: INVOICE_RECURRENT
                        status: DRAFT
                        company: { connect: { id: $company } }
                        client: $createClient
                        owner: $createOwner
                        endDate: $endDate
                        startDate: $startDate
                        periodicity: $periodicity
                        paymentTerms: $paymentTerms
                        dueDateAmount: $dueDateAmount
                        dueDateUnitOfTime: $unitOfTime
                        discount: $discount
                        discountPercentage: $discountPercentage
                        lines: { create: $linesToCreate }
                        # tags: {
                        #     create: $tagsToCreate
                        # }
                        vatType: $vatType
                        autoValidation: $autoValidation
                        sendEmailAfterValidation: $sendEmailAfterValidation
                        netAmount: $netAmount
                        grossAmount: $grossAmount
                        vatAmount: $vatAmount
                        data: $data
                    }
                    update: {
                        title: $title
                        client: $updateClient
                        endDate: $endDate
                        startDate: $startDate
                        periodicity: $periodicity
                        paymentTerms: $paymentTerms
                        dueDateAmount: $dueDateAmount
                        dueDateUnitOfTime: $unitOfTime
                        discount: $discount
                        discountPercentage: $discountPercentage
                        lines: { create: $linesToCreate, update: $linesToUpdate }
                        # tags: {
                        #     create: $tagsToCreate
                        #     update: $tagsToUpdate
                        # }
                        vatType: $vatType
                        autoValidation: $autoValidation
                        sendEmailAfterValidation: $sendEmailAfterValidation
                        netAmount: $netAmount
                        grossAmount: $grossAmount
                        vatAmount: $vatAmount
                        data: $data
                    }
                ) {
                    id
                }
            }
        `;
        const result = await this.$apollo.mutate({
            mutation: save_mutation,
            variables: {
                invoiceId: invoiceId,
                title: this.invoice.title,
                periodicity: this.invoice.periodicity,
                company: this.selectedCompany.id,
                createClient: this.invoice.client.id
                    ? { connect: { id: this.invoice.client.id } }
                    : null,
                updateClient: this.invoice.client.id
                    ? { connect: { id: this.invoice.client.id } }
                    : null,
                createOwner: this.$store.state?.user
                    ? { connect: { id: this.$store.state.user.id } }
                    : null,
                startDate: getDBFormattedDate(this.invoice.startDate),
                endDate: this.isInfinitReccurency ? null : getDBFormattedDate(this.invoice.endDate),
                day: this.selectedDay,
                dueDateAmount: this.dueDateAmount,
                unitOfTime: this.unitOfTime,
                paymentTerms: undefined,
                data: this.invoice.data,
                discount: this.invoice.discount,
                discountPercentage: this.invoice.discountPercentage,
                linesToCreate: linesToCreate,
                linesToUpdate: linesToUpdate,
                // tagsToUpdate: tagsToUpdate, à faire ???
                vatType: this.invoice.vatType,
                autoValidation: this.invoice.autoValidation || false,
                sendEmailAfterValidation: this.invoice.sendEmailAfterValidation || false,
                netAmount: this.netAmountTotal,
                grossAmount: this.grossAmountTotal,
                vatAmount: this.vatAmountTotal,
            },
        });
        this.isSaving = false;
        if (!invoiceId && result.data && result.data.upsertInvoice) {
            this.invoice.id = result.data.upsertInvoice.id;
        }
    }

    get displayDepositSwitch() {
        if (
            this.invoice.type === "INVOICE" &&
            this.invoice.status !== "VALIDATED" &&
            !this.invoice.depositOf
        ) {
            // condition sur facture de vente ? ERIC ???
            return true;
        }
        this.depositSwitch = false;
        return false;
    }

    generateDepositloading = false;

    async generateDeposits() {
        console.log("generateDeposits start");
        this.generateDepositloading = true;
        // generate deposits
        // validate devis
        //await this.saveInvoice(true, 'INVOICE')
        const formattedDeposits = this.newDeposits.map((dep) => ({
            ...dep,
            date: dep.date ? moment(dep.date).format() : "",
        }));
        await this.$apollo.mutate({
            mutation: GENERATE_DEPOSITS,
            variables: {
                newDeposits: formattedDeposits,
                invoiceId: this.invoice.id || undefined,
            },
        });
        this.generateDepositloading = false;
        this.convertDocumentDialog = false;
        this.refresh();
    }

    getPercentageOfDeposit(deposit: Invoice): number | "" {
        if (!this.invoice.depositOf.grossAmount || !deposit.lines?.[0]?.grossAmount) {
            return "";
        } else {
            return Math.round(
                (deposit.lines[0].grossAmount / this.invoice.depositOf.grossAmount) * 100,
            );
        }
    }

    async saveInvoice(duplicate: boolean = false, forceFormat: InvoiceType = null) {
        if (duplicate) {
            this.dialogConfirmDuplicate = false;
        }
        this.convertDocumentDialog = false;
        const newInvoice = !this.invoice.id;
        await this._saveInvoice(duplicate, forceFormat);
        if (newInvoice || duplicate) {
            this.updatePath(duplicate, forceFormat);
        }
    }

    @Apollo({
        query: LAST_INVOICE_DATE,
        variables() {
            return {
                id: this.companyId,
                type: this.invoice.type || "INVOICE",
            };
        },
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading }) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data && data.lastInvoiceDate) {
                    this.lastDateInvoice = data.lastInvoiceDate.date;
                    if (!this.allowedDates(this.invoice.date)) {
                        this.invoice.date = moment(this.lastDateInvoice).format("YYYY-MM-DD");
                        this.onDateChanged(this.invoice.date);
                    }
                } else {
                    this.lastDateInvoice = null;
                }
            }
        },
    })
    lastDateInvoice = null;

    allowedDates(date) {
        if (!this.lastDateInvoice) {
            return true;
        }
        return moment(date) >= moment(this.lastDateInvoice, "YYYY-MM-DD");
    }

    allowedDueDate(date) {
        return moment(date) >= moment(this.invoice.date, "YYYY-MM-DD");
    }

    async setConfirmInvoiceFirstStep() {
        const type = this.getInvoiceType();

        // if matrix avoid chrono wizzard
        if (this.isInvoiceMatrix) {
            this.confirmInvoiceStep = 4;
            this.confirmInvoiceInitialStep = 4;
            this.dialogConfirmInvoice = true;
            return;
        }

        // the base to find the right chrono
        let baseFieldName = "";
        let defaultChrono = "";

        // set the baseFieldName for the search of the right chrono
        switch (type) {
            case "QUOTATION":
                baseFieldName = "quote";
                defaultChrono = "DEVIS-YYYYMM-XXXXXXX";
                break;
            case "CREDIT_NOTE":
                baseFieldName = "creditNote";
                defaultChrono = "AVOIR-YYYYMM-XXXXXXX";
                break;
            case "BILL_DELIVERY":
                baseFieldName = "deliveryBill";
                defaultChrono = "DB-YYYYMM-XXXXXXX";
                break;
            case "INVOICE":
                if (this.isDeposit) {
                    baseFieldName = "deposit";
                    defaultChrono = "ACOMPTE-YYYYMM-XXXXXXX";
                } else {
                    baseFieldName = "invoice";
                    defaultChrono = "FACTURE-YYYYMM-XXXXXXX";
                }
                break;
        }

        // if chrono don't exist begin at the very start of the wizzard
        if (!this.invoiceData?.chrono) {
            this.confirmInvoiceStep = 1;
            this.confirmInvoiceInitialStep = 1;
            this.confirmInvoiceData.chrono = defaultChrono;
            this.confirmInvoiceData.resetChronoCondition = "NONE";
            this.confirmInvoiceData.counter = 1;
            this.dialogConfirmInvoice = true;
            return;
        }

        // if the chrono don't exist begin to the very start of the wizzard chrono if not, set the chrono variable for the next
        if (!this.invoiceData.chrono[baseFieldName + "Chrono"]) {
            this.confirmInvoiceStep = 1;
            this.confirmInvoiceInitialStep = 1;
            this.confirmInvoiceData.chrono = defaultChrono;
            this.confirmInvoiceData.resetChronoCondition = "NONE";
            this.confirmInvoiceData.counter = 1;
            this.dialogConfirmInvoice = true;
            return;
        } else {
            this.confirmInvoiceData.chrono = this.invoiceData.chrono[baseFieldName + "Chrono"];
            this.confirmInvoiceData.counter = this.invoiceData.counter[baseFieldName];
            this.confirmInvoiceData.resetChronoCondition =
                this.invoiceData.chrono[baseFieldName + "ResetCounterCondition"];
        }

        // search the invoice validated by type
        let invoiceResponse: GetCompanyLastInvoiceValidatedResponse;

        if (baseFieldName === "deposit") {
            invoiceResponse = await this.$apollo.query({
                query: GET_COMPANY_LAST_DEPOSIT_VALIDATED,
                variables: {
                    companyId: this.companyId,
                },
            });
        } else {
            invoiceResponse = await this.$apollo.query({
                query: GET_COMPANY_LAST_INVOICE_VALIDATED,
                variables: {
                    companyId: this.companyId,
                    type: type,
                },
            });
        }

        const invoice = invoiceResponse.data;
        switch (this.confirmInvoiceData.resetChronoCondition) {
            case "NONE":
                // if invoice found, the chrono is already set and the counter too, else we need to set it
                if (invoice.invoices.length) {
                    this.confirmInvoiceStep = 4;
                    this.confirmInvoiceInitialStep = 4;
                } else {
                    this.confirmInvoiceStep = 1;
                    this.confirmInvoiceInitialStep = 1;
                    this.confirmInvoiceData.chrono = defaultChrono;
                    this.confirmInvoiceData.resetChronoCondition = "NONE";
                    this.confirmInvoiceData.counter = 1;
                }
                break;
            case "MONTH":
                if (!invoice.invoices.length) {
                    this.confirmInvoiceStep = 1;
                    this.confirmInvoiceInitialStep = 1;
                    this.confirmInvoiceData.chrono = defaultChrono;
                    this.confirmInvoiceData.resetChronoCondition = "MONTH";
                    this.confirmInvoiceData.counter = 1;
                } else {
                    const date = new Date(this.invoice.date);
                    const year = date.getFullYear();
                    const month = date.getMonth();
                    const actualYear = new Date().getFullYear();
                    const actualMonth = new Date().getMonth();

                    if (
                        year < actualYear &&
                        (!this.confirmInvoiceData.counter[year] ||
                            !this.confirmInvoiceData.counter[year][month])
                    ) {
                        this.confirmInvoiceStep = 3;
                        this.confirmInvoiceInitialStep = 3;
                        this.confirmInvoiceData.counter = 1;
                    } else if (
                        year === actualYear &&
                        month < actualMonth &&
                        !this.confirmInvoiceData.counter[year][month]
                    ) {
                        this.confirmInvoiceStep = 3;
                        this.confirmInvoiceInitialStep = 3;
                        this.confirmInvoiceData.counter = 1;
                    } else {
                        this.confirmInvoiceStep = 4;
                        this.confirmInvoiceInitialStep = 4;
                    }
                }
                break;
            case "YEAR":
                if (!invoice.invoices.length) {
                    this.confirmInvoiceStep = 1;
                    this.confirmInvoiceInitialStep = 1;
                    this.confirmInvoiceData.chrono = defaultChrono;
                    this.confirmInvoiceData.resetChronoCondition = "YEAR";
                    this.confirmInvoiceData.counter = 1;
                } else {
                    const year = new Date(this.invoice.date).getFullYear();
                    const actualYear = new Date().getFullYear();

                    if (year < actualYear && !this.confirmInvoiceData.counter[year]) {
                        this.confirmInvoiceStep = 3;
                        this.confirmInvoiceInitialStep = 3;
                        this.confirmInvoiceData.counter = 1;
                    } else {
                        this.confirmInvoiceInitialStep = 4;
                        this.confirmInvoiceStep = 4;
                    }
                }
                break;
        }
        this.dialogConfirmInvoice = true;
    }

    async verifInvoiceDate() {
        this.isSaving = true;
        await this.saveInvoice();

        if (this.newDateAccepted) {
            await this.setConfirmInvoiceFirstStep();
            this.isSaving = false;
            return;
        }
        if (!this.lastDateInvoice) {
            await this.setConfirmInvoiceFirstStep();
            this.isSaving = false;
            return;
        }
        if (moment(this.invoice.date).isSameOrAfter(moment(this.lastDateInvoice).startOf("day"))) {
            await this.setConfirmInvoiceFirstStep();
            this.isSaving = false;
            return;
        }
        this.errorDateDialog = true;
        this.isSaving = false;
    }

    newDateAccepted: boolean = false;

    updatePath(duplicate: boolean = false, forceFormat: InvoiceType = null) {
        let route = "invoiceEdition";
        if (forceFormat) {
            switch (forceFormat) {
                case "INVOICE":
                case "INVOICE_MATRIX":
                    route = "invoiceEdition";
                    break;
                case "QUOTATION":
                    route = "quotationEdition";
                    break;
                case "BILL_DELIVERY":
                    route = "billDeliveryEdition";
                    break;
            }
        } else if (this.isQuote) route = "quotationEdition";
        else if (this.isBillOfDelivery) route = "billDeliveryEdition";
        if (duplicate) {
            this.$router.replace({
                name: route,
                params: { id: this.invoice.id, companyId: this.companyId },
            });
        } else {
            this.$router.push({
                name: route,
                params: { id: this.invoice.id, companyId: this.companyId },
            });
        }
    }

    async createCreditNote() {
        this.isSaving = true;

        const createdCreditNote = await this._createSameInvoice(
            "Avoir pour la facture " + this.invoice.number,
        );

        this.isSaving = false;
        this.$router.replace({
            name: "invoiceEdition",
            params: { id: createdCreditNote.data.createInvoice.id, companyId: this.companyId },
        });
    }

    async _createSameInvoice(newTitle: string) {
        this.invoice.data.legalText = this.invoice.data.legalText || "";
        this.invoice.data.paymentDetails = this.invoice.data.paymentDetails || "";
        this.invoice.data.paymentMode = this.invoice.data.paymentMode || "";
        this.invoice.data ??= {};

        const client =
            this.invoice.client && this.invoice.client.id
                ? { connect: { id: this.invoice.client.id } }
                : null;
        // const lines_to_create = this.invoice.lines.filter(l => (!l.id || l.id.indexOf('new-') === 0))
        const linesToCreate = this.invoice.lines.map((l) => {
            let productId;
            if (l.product) {
                productId = l.product.id;
            } else {
                const linked_product = l.reference
                    ? this.products.find((e) => e.reference === l.reference)
                    : undefined;
                if (linked_product) {
                    productId = linked_product.id;
                }
            }
            return {
                reference: l.reference,
                title: l.title,
                type: l.type,
                quantity: l.quantity,
                description: l.description,
                unitPrice: l.unitPrice,
                netAmount: l.netAmount,
                grossAmount: l.grossAmount,
                vatAmount: l.vatAmount,
                vatRate: l.vatRate,
                product: productId
                    ? {
                          connect: { id: productId },
                      }
                    : undefined,
            };
        });

        const type = "CREDIT_NOTE";

        const save_mutation = gql`
            mutation createInvoice(
                $title: String!
                $companyId: ID!
                $invoiceType: InvoiceType
                $client: CompanyCreateOneInput
                $date: DateTime!
                $dueDate: DateTime!
                $paymentTerms: InvoicePaymentTerms
                $lines: [InvoiceLineCreateInput!]!
                $data: Json!
                $invoiceid: ID!
            ) {
                createInvoice(
                    data: {
                        title: $title
                        status: DRAFT
                        type: $invoiceType
                        company: { connect: { id: $companyId } }
                        client: $client
                        date: $date
                        creditNoteOf: { connect: { id: $invoiceid } }
                        dueDate: $dueDate
                        paymentTerms: $paymentTerms
                        data: $data
                        lines: { create: $lines }
                    }
                ) {
                    id
                }
            }
        `;

        // let receivers = undefined
        // if (this.invoice?.receiverEmail || this.invoice?.receiver?.lastname || this.invoice?.receiver?.firstname) {
        //     receivers = [{ email: this.invoice.receiverEmail || '', ...this.invoice.receiver, phoneNumber: '' }]
        // }

        return await this.$apollo.mutate({
            mutation: save_mutation,
            variables: {
                title: newTitle,
                invoiceType: type,
                companyId: this.selectedCompany.id,
                client: client,
                date: getDBFormattedDate(this.invoice.date),
                dueDate: getDBFormattedDate(this.invoice.dueDate),
                paymentTerms: undefined,
                // paymentDetails: this.invoice.paymentDetails,
                // receivers: receivers,
                // receiver: this.invoice.receiver,
                // receiverEmail: this.invoice.receiverEmail,
                // legalText: this.invoice.legalText,
                lines: linesToCreate,
                invoiceid: this.invoice.id,
                data: this.invoice.data,
            },
        });
    }

    async cancelAndReplaceInvoice() {
        this.isSaving = true;

        const result = await this.$apollo.mutate({
            mutation: CANCEL_AND_REPLACE_INVOICE,
            variables: {
                invoiceId: this.invoice.id,
            },
        });

        this.isSaving = false;
        this.cancelAndReplaceDialog = false;
        this.$router.replace({
            name: "invoiceEdition",
            params: { id: result.data.cancelAndReplaceInvoice.id, companyId: this.companyId },
        });
    }

    async _saveInvoice(duplicate: boolean = false, forceFormat: InvoiceType = null) {
        this.isSaving = true;

        this.invoice.data.legalText ||= "";
        this.invoice.data.paymentDetails ||= "";
        this.invoice.data.paymentMode ||= "";
        // this.invoice.data ||= {}

        const client =
            this.invoice.client && this.invoice.client.id
                ? { connect: { id: this.invoice.client.id } }
                : null;

        if (this.isRecurrent || this.gonnaBeRecurrenceInvoice) {
            const newLines = this.invoice.lines.filter((line) => line.netAmount);
            await this.upsertReccurentInvoice(duplicate, newLines);
            return;
        }

        let dueDate = null;
        if (this.isQuote && forceFormat === "INVOICE") {
            dueDate = this.invoice.date;
        } else {
            dueDate = this.invoice.dueDate;
        }
        if (this.isInvoiceMatrix) {
            await this.upsertInvoiceMatrix(duplicate);
            return;
        }
        // let invoiceData = undefined
        if (this.newDeposits?.length >= 2) {
            // rajouter les conditions // ERIC ??? Maxime
            this.invoice.data ||= {};
            this.invoice.data["deposits"] = this.newDeposits;
        }
        if (!this.depositSwitch && this.invoice.data?.deposits) {
            delete this.invoice.data.deposits;
        }
        const type = this.getInvoiceType(forceFormat);

        if (!this.invoice.id || duplicate) {
            const save_mutation = gql`
                mutation createInvoice(
                    $title: String
                    $companyId: ID!
                    $invoiceType: InvoiceType
                    $client: CompanyCreateOneInput
                    $ownerId: ID!
                    $date: DateTime!
                    $dueDate: DateTime!
                    $paymentTerms: InvoicePaymentTerms
                    $data: Json!
                    $discount: Float
                    $discountPercentage: Float
                    $lines: [InvoiceLineCreateInput!]!
                    $vatType: VATType
                    $netAmount: Float
                    $vatAmount: Float
                    $grossAmount: Float
                    $sendEmailAfterValidation: Boolean
                ) {
                    createInvoice(
                        data: {
                            title: $title
                            status: DRAFT
                            type: $invoiceType
                            company: { connect: { id: $companyId } }
                            owner: { connect: { id: $ownerId } }
                            client: $client
                            date: $date
                            vatType: $vatType
                            netAmount: $netAmount
                            vatAmount: $vatAmount
                            grossAmount: $grossAmount
                            # receiver: $receiver
                            # receiverEmail: $receiverEmail
                            dueDate: $dueDate
                            paymentTerms: $paymentTerms
                            data: $data
                            discount: $discount
                            discountPercentage: $discountPercentage
                            lines: { create: $lines }
                            sendEmailAfterValidation: $sendEmailAfterValidation
                        }
                    ) {
                        id
                        lines {
                            id
                        }
                    }
                }
            `;

            // let receivers = undefined
            // if (this.invoice.receiverEmail || (this.invoice.receiver && (this.invoice.receiver.lastname || this.invoice.receiver.firstname))) {
            //     receivers = [ ...this.invoice.data.receivers ]
            // }
            const result = await this.$apollo.mutate({
                mutation: save_mutation,
                variables: {
                    title: this.invoice.title,
                    invoiceType: type,
                    companyId: this.selectedCompany.id,
                    client: client,
                    ownerId: this.$store.state?.user ? this.$store.state.user.id : null,
                    date: getDBFormattedDate(this.invoice.date),
                    dueDate: getDBFormattedDate(dueDate),
                    paymentTerms: undefined,
                    data: this.invoice.data,
                    vatType: this.invoice.vatType,
                    netAmount: this.netAmountTotal,
                    vatAmount: this.vatAmountTotal,
                    grossAmount: this.grossAmountTotal,
                    discount: this.invoice.discount,
                    discountPercentage: this.invoice.discountPercentage,
                    lines: this.invoice.lines.map((line) => ({
                        reference: line.reference,
                        title: line.title,
                        type: line.type,
                        description: line.description,
                        quantity: line.quantity,
                        unitPrice: line.unitPrice,
                        netAmount: line.netAmount,
                        grossAmount: line.grossAmount,
                        vatAmount: line.vatAmount,
                        vatRate: line.vatRate,
                        product: line?.product?.id
                            ? {
                                  connect: { id: line.product.id },
                              }
                            : undefined,
                        discount: line.discount,
                        discountPercentage: line.discountPercentage,
                        isOptional: line.isOptional,
                    })),
                    sendEmailAfterValidation: this.invoice.sendEmailAfterValidation,
                    // tags: this.tags
                },
            });
            if (result && result.data && result.data.createInvoice) {
                this.invoice.id = result.data.createInvoice.id;
                this.invoice.lines.map((line, index) => {
                    line.id = result.data.createInvoice.lines[index].id;
                });
            }
            this.isSaving = false;
        } else {
            // update/create lines
            const update_mutation = gql`
                mutation updateInvoice(
                    $invoiceId: ID!
                    $title: String
                    $type: InvoiceType!
                    $client: CompanyUpdateOneInput
                    $date: DateTime
                    $dueDate: DateTime
                    $paymentTerms: InvoicePaymentTerms
                    #   $paymentDetails: String!, $legalText: String!,
                    $discount: Float
                    $discountPercentage: Float
                    #   $linesUpdate: [InvoiceLineUpdateWithWhereUniqueNestedInput!]!,
                    $linesCreate: [InvoiceLineCreateInput!]!
                    $linesDelete: [InvoiceLineScalarWhereInput!]
                    #    $receiver: Json, $receiverEmail: String,
                    #   $receivers: Json,
                    #    $tags: TagUpdateManyInput
                    #    $tagsUpdate: [TagUpdateWithWhereUniqueNestedInput!]!,
                    #    $tagsCreate: [TagCreateInput!]!,
                    $vatType: VATType
                    $netAmount: Float
                    $vatAmount: Float
                    $grossAmount: Float
                    $data: Json!
                    $sendEmailAfterValidation: Boolean
                ) {
                    #   $additionalComment: String,
                    #   $internalComment: String) {
                    updateInvoice(
                        where: { id: $invoiceId }
                        data: {
                            title: $title
                            type: $type
                            # additionalComment: $additionalComment
                            # internalComment: $internalComment
                            client: $client
                            # receiver: $receiver
                            # receiverEmail: $receiverEmail
                            # receivers: $receivers
                            date: $date
                            data: $data
                            vatType: $vatType
                            netAmount: $netAmount
                            vatAmount: $vatAmount
                            grossAmount: $grossAmount
                            dueDate: $dueDate
                            paymentTerms: $paymentTerms
                            # paymentDetails: $paymentDetails
                            # legalText: $legalText
                            discount: $discount
                            discountPercentage: $discountPercentage
                            lines: { create: $linesCreate, deleteMany: $linesDelete }
                            # tags: {
                            #     update: $tagsUpdate,
                            #     create: $tagsCreate,
                            #     delete: $linesDelete
                            # }
                            sendEmailAfterValidation: $sendEmailAfterValidation
                        }
                    ) {
                        id
                        lines {
                            id
                        }
                    }
                }
            `;

            // let receivers = undefined
            // if (this.invoice.data.receivers?.length && (this.invoice.data.receivers[0].email || this.invoice.data.receivers[0].lastname || this.invoice.data.receivers[0].firstname)) {
            //     receivers = [ ...this.invoice.data.receivers ]
            // }
            const result = await this.$apollo.mutate({
                mutation: update_mutation,
                variables: {
                    invoiceId: this.invoice.id,
                    title: this.invoice.title || "Sans titre",
                    type: this.getInvoiceType(), // this.invoice.type
                    // additionalComment: this.invoice.additionalComment,
                    // internalComment: this.invoice.internalComment,
                    client: client,
                    date: getDBFormattedDate(this.invoice.date),
                    data: this.invoice.data,
                    // receiver: this.invoice.receiver,
                    // receiverEmail: this.invoice.receiverEmail,
                    // receivers: receivers,
                    dueDate: getDBFormattedDate(this.invoice.dueDate),
                    paymentTerms: undefined,
                    vatType: this.invoice.vatType,
                    netAmount: this.netAmountTotal,
                    vatAmount: this.vatAmountTotal,
                    grossAmount: this.grossAmountTotal,

                    discount: this.invoice.discount,
                    discountPercentage: this.invoice.discountPercentage,
                    // linesUpdate: linesToUpdate,
                    linesCreate: this.invoice.lines.map((line) => ({
                        reference: line.reference,
                        title: line.title,
                        type: line.type,
                        description: line.description,
                        quantity: line.quantity,
                        unitPrice: line.unitPrice,
                        netAmount: line.netAmount,
                        grossAmount: line.grossAmount,
                        vatAmount: line.vatAmount,
                        vatRate: line.vatRate,
                        product: line?.product?.id
                            ? {
                                  connect: { id: line.product.id },
                              }
                            : undefined,
                        discount: line.discount,
                        discountPercentage: line.discountPercentage,
                        isOptional: line.isOptional,
                    })),
                    linesDelete: [
                        ...this.invoice.lines.filter((l) => !!l.id).map((l) => ({ id: l.id })),
                        ...this.linesToDelete,
                    ],
                    sendEmailAfterValidation: this.invoice.sendEmailAfterValidation,
                    // tagsCreate: this.tags,
                    // tagsUpdate: this.tags.map(t => { id: t.id }) //tagsToUpdate
                },
            });
            // let idResult = 0;
            this.invoice.lines.map((line, index) => {
                line.id = result.data.updateInvoice.lines[index].id;
            });
            this.linesToDelete = [];
            this.isSaving = false;
        }
    }

    reccurencyDialog = false;

    get reccurencyType() {
        return [
            { text: this.$t("invoice.reccurency.type.annual"), value: "ANNUAL" },
            { text: this.$t("invoice.reccurency.type.biannual"), value: "BIANNUAL" },
            { text: this.$t("invoice.reccurency.type.quarterly"), value: "QUARTERLY" },
            { text: this.$t("invoice.reccurency.type.monthly"), value: "MONTHLY" },
            { text: this.$t("invoice.reccurency.type.bimonthly"), value: "BIMONTHLY" },
        ];
    }

    formatPeriodicity(value) {
        return this.reccurencyType.find((e) => e.value === value).text;
    }

    formatReccurenceDate() {
        if (this.invoice.startDate) {
            switch (this.invoice.periodicity) {
                case "ANNUAL": {
                    return this.$t("invoice.reccurency.reccurencyText.annual", {
                        date: moment(this.invoice.startDate).format("DD MMMM"),
                    });
                }
                case "MONTHLY": {
                    if (this.selectedDay !== -1) {
                        return this.$t("invoice.reccurency.reccurencyText.monthly", {
                            date: moment(this.invoice.startDate).format("DD"),
                        });
                    }
                    return this.$t("invoice.reccurency.reccurencyText.endMonthly");
                }
                case "BIANNUAL": {
                    return this.$t("invoice.reccurency.reccurencyText.biannual", {
                        date1: moment(this.invoice.startDate).format("DD MMMM"),
                        date2: moment(this.invoice.startDate).add("months", 6).format("DD MMMM"),
                    });
                }
                case "QUARTERLY": {
                    return this.$t("invoice.reccurency.reccurencyText.quarterly", {
                        date1: moment(this.invoice.startDate).format("DD MMMM"),
                        date2: moment(this.invoice.startDate).add("months", 4).format("DD MMMM"),
                        date3: moment(this.invoice.startDate).add("months", 8).format("DD MMMM"),
                    });
                }
                case "BIMONTHLY": {
                    return this.$t("invoice.reccurency.reccurencyText.bimonthly", {
                        date1: moment(this.invoice.startDate).format("DD"),
                        date2: moment(this.invoice.startDate).add("days", 14).format("DD"),
                    });
                }
            }
        }
        return this.$t("invoice.reccurency.reccurencyText.default");
    }

    selectedDay = null;

    setDayPeriodicity() {
        const nextDay = moment();
        if (this.selectedDay !== -1) {
            if (nextDay.day() > this.selectedDay) {
                nextDay.add(1, "M");
            }
            nextDay.set("date", this.selectedDay);
        } else {
            nextDay.add("months", 1).date(1).subtract("days", 1);
        }
        this.invoice.nextDate = moment(nextDay).format("YYYY-MM-DD");
    }

    get daysPeriodicity() {
        return [
            { text: "Fin de chaque mois", value: -1 },
            { text: "01", value: 1 },
            { text: "02", value: 2 },
            { text: "03", value: 3 },
            { text: "04", value: 4 },
            { text: "05", value: 5 },
            { text: "06", value: 6 },
            { text: "07", value: 7 },
            { text: "08", value: 8 },
            { text: "09", value: 9 },
            { text: "10", value: 10 },
            { text: "11", value: 11 },
            { text: "12", value: 12 },
            { text: "13", value: 13 },
            { text: "14", value: 14 },
            { text: "15", value: 15 },
            { text: "16", value: 16 },
            { text: "17", value: 17 },
            { text: "18", value: 18 },
            { text: "19", value: 19 },
            { text: "20", value: 20 },
            { text: "21", value: 21 },
            { text: "22", value: 22 },
            { text: "23", value: 23 },
            { text: "24", value: 24 },
            { text: "25", value: 25 },
            { text: "26", value: 26 },
            { text: "27", value: 27 },
            { text: "28", value: 28 },
            { text: "29", value: 29 },
            { text: "30", value: 30 },
            { text: "31", value: 31 },
        ];
    }

    get weekPeriodicity() {
        return [
            { text: "Fin de chaque mois", value: -1 },
            { text: "01", value: 1 },
            { text: "02", value: 2 },
            { text: "03", value: 3 },
            { text: "04", value: 4 },
            { text: "05", value: 5 },
            { text: "06", value: 6 },
            { text: "07", value: 7 },
            { text: "08", value: 8 },
            { text: "09", value: 9 },
            { text: "10", value: 10 },
            { text: "11", value: 11 },
            { text: "12", value: 12 },
            { text: "13", value: 13 },
            { text: "14", value: 14 },
            { text: "15", value: 15 },
            { text: "16", value: 16 },
            { text: "17", value: 17 },
            { text: "18", value: 18 },
            { text: "19", value: 19 },
            { text: "20", value: 20 },
            { text: "21", value: 21 },
            { text: "22", value: 22 },
            { text: "23", value: 23 },
            { text: "24", value: 24 },
            { text: "25", value: 25 },
            { text: "26", value: 26 },
            { text: "27", value: 27 },
            { text: "28", value: 28 },
            { text: "29", value: 29 },
            { text: "30", value: 30 },
            { text: "31", value: 31 },
        ];
    }

    async deleteDocument() {
        this.deleteLoading = true;
        await this.$apollo.mutate({
            mutation: gql`
                mutation deleteInvoice($docId: ID!) {
                    deleteInvoice(where: { id: $docId }) {
                        id
                    }
                }
            `,
            variables: {
                docId: this.invoice.id,
            },
        });
        this.deleteLoading = false;
        this.$router.push({ name: "invoices" });
    }

    checkCompany() {
        console.log("userCompany", this.userCompany);
        if (!this.userCompany) return false;
        if (!this.userCompany.siret) return false;
        if (!this.userCompany.address) return false;
        if (!this.userCompany.postalCode) return false;
        if (!this.userCompany.city) return false;
        if (!this.userCompany.legalForm) return false;
        if (
            !["ASSO", "CL", "CNE", "FEDE", "FOND", "IND", "MUT"].includes(
                this.userCompany.legalForm,
            )
        ) {
            if (!this.userCompany.registrationTown) return false;
        }
        return true;
    }

    checkClient() {
        if (!this.invoice.client) return false;
        if (!this.invoice.client.name) return false;
        return true;
    }

    get isFromRecurrenceInvoice(): boolean {
        if (this.invoice.reccurenceInvoice?.id) {
            return true;
        }
        return false;
    }

    get isDeposit(): boolean {
        if (this.invoice?.depositOf?.id) {
            return true;
        }
        return false;
    }

    get isDepositOfInvoice(): boolean {
        if (this.isDeposit && this.invoice?.depositOf.type === "INVOICE") {
            return true;
        }
        return false;
    }

    get isDepositOfQuotation(): boolean {
        if (this.isDeposit && this.invoice?.depositOf.type === "QUOTATION") {
            return true;
        }
        return false;
    }

    get areAllDepositLines(): boolean {
        if (this.invoice.lines) {
            if (this.invoice.lines.every((l) => l.type === "DEPOSIT")) {
                return true;
            }
        }
        return false;
    }

    get disabledValidate() {
        if (this.invoice.status === "VALIDATED") return true;
        if (!this.invoice.data.legalText) return true;
        if (!this.checkCompany()) return true;
        const date = this.isRecurrent ? this.invoice.startDate : this.invoice.date;
        if (!date) return true;
        if (!this.areRulesValid) return true;
        if (this.lastDateInvoice) {
            if (moment(date).startOf("day").isBefore(moment(this.lastDateInvoice).startOf("day"))) {
                return true;
            }
        }
        if (moment(this.invoice.dueDate).diff(moment(this.invoice.date)) < 0) return true;
        if (this.isRecurrent) {
            if (
                !(
                    this.invoice.startDate &&
                    (this.invoice.endDate || this.isInfinitReccurency) &&
                    this.invoice.periodicity
                )
            ) {
                return true;
            }
        }
        if (this.isInvoiceMatrix) {
            if (!this.invoiceMatrixData?.matrix?.length) {
                return true;
            }
            for (const clientData of this.invoiceMatrixData.matrix) {
                if (clientData.selected) {
                    for (const quantity of clientData.quantities) {
                        if (quantity.quantity) {
                            return false;
                        }
                    }
                }
            }
            return true;
        } else {
            if (this.depositSwitch && !this.depositIsValid) return true;
            if (!this.checkClient()) return true;
            if (!this.invoice.lines.length) return true;
        }
        if (isInsideClosedAccountingPeriod(this.$store, this.invoice.date)) {
            return true;
        }

        return false;
    }

    get disabledDownload() {
        // if (!this.checkCompany()) return true;
        if (!this.checkClient()) return true;
        if (!this.invoice.lines.length) return true;

        if (this.invoice.status === "BEING_VALIDATED") return true;
        if (this.localStepData?.action) return true;
        return false;
    }

    get disabledCreateDocument() {
        if (!this.invoice.client) return true;
        if (
            (this.invoice.status !== "VALIDATED" && this.invoice.status !== "BEING_VALIDATED") ||
            this.invoice.document
        )
            return true;
        if (!this.invoice.data.legalText) return true;
        if (!this.checkCompany()) return true;
        if (!this.invoice.lines.length) return true;
        return false;
    }

    test = false;

    get displayTTCAmounts() {
        return false;
        // return this.test
        // if (this.selectedCompany?.thirdPartyCompanyData?.amountsDisplayed_TTC)  //  remove thirdPartyCompanyData from user apollo request
        // const thisThirdPartyCompanyData: any = this.userCompany?.thirdPartyCompanyData ? this.userCompany.thirdPartyCompanyData : {}
        // if(thisThirdPartyCompanyData.amountsDisplayedTTC) {
        //     return true
        // }
        // return false
    }

    formatNetAmount(netAmount) {
        if (this.displayTTCAmounts) {
            return "";
        } else {
            return newCommonFormatCurrency(netAmount, undefined);
        }
    }

    setDefaultLegalForm() {
        this.invoice.data.legalText = defaultLegalForm(this.userCompany);
    }

    // RECURSION_PROBLEM: possible recursion problem in vue 3 as the dependency invoice is modified in the watcher
    // @Watch("invoice", { deep: true })
    // selectVATTypeManager_old() {
    //     const vatRates = this.invoice.lines.map((x) => x.vatRate).filter((x) => x === 0);
    //     if (vatRates.length) {
    //         if (!this.invoice.vatType) {
    //             this.invoice.vatType = "NO_VAT_TYPE";
    //         }
    //         this.selectVATType = true;
    //     } else {
    //         this.selectVATType = false;
    //         this.invoice.vatType = null;
    //     }
    // }

    /**
     * Watch the invoice.lines to adapt the vatType to the invoice
     * If there is at least one line with a vatRate of 0, then the vatType is set to "NO_VAT_TYPE" and the selectVATType is set to true
     * Otherwise, the vatType is set to null and the selectVATType is set to false
     */
    @Watch("invoice.lines", { deep: true })
    selectVATTypeManager() {
        const vatRates = this.invoice.lines.map((x) => x.vatRate).filter((x) => x === 0);
        if (vatRates.length) {
            if (!this.invoice.vatType) {
                this.invoice.vatType = "NO_VAT_TYPE";
            }
            this.selectVATType = true;
        } else {
            this.selectVATType = false;
            this.invoice.vatType = null;
        }
    }

    // @Watch('invoice.lines', { deep: true })
    // adaptTotalsToDiscounts() {
    //     for(const line of this.invoice.lines) {
    //         const discountAmount = line.discount ? line.discount : line.discountPercentage ? (line.discountPercentage / 100) * line.netAmount : 0
    //         line.netAmountAfterDiscount = line.netAmount - discountAmount
    //         line.vatAmount = (line.vatRate === null && line.type === 'DEPOSIT') ? line.vatAmount : mathFix( line.netAmountAfterDiscount * (line.vatRate / 100 ), this.maxDeciamalAmount)
    //         line.grossAmount = line.netAmountAfterDiscount + line.vatAmount
    //     }
    // }

    onMessage(data) {
        // console.log("on message document")
        if (data?.id === this.invoice?.id) {
            const updatedInvoice = data.payload;
            // if (this.document.type !== updatedInvoice.type || this.document.status !== updatedInvoice.status || !updatedInvoice.status?.startsWith('ANALYSED')) {
            if (
                this.invoice.status === "BEING_VALIDATED" &&
                updatedInvoice.status === "VALIDATED"
            ) {
                this.invoice.status = updatedInvoice.status;
                this.invoice.document = updatedInvoice.document;
            }
            this.isSaving = false;
            if (data.extraPayload?.signedUrl) {
                this.stepData_ = null;
                this.downloadLoading = false;
                window.open(data.extraPayload.signedUrl, "_blank");
            } else {
                // console.log(data.extraPayload)
                this.stepData_ = data.extraPayload;
                if (this.stepData_ && this.stepData_.step >= this.stepData_.stepCount) {
                    this.stepData_ = null;
                    this.downloadLoading = false;
                    this.refresh();
                } else {
                    this.downloadLoading = true;
                }
            }
        }
    }

    stepData_ = null;
    downloadLoading: boolean = false;

    get localStepData() {
        if (this.stepData_) {
            return this.stepData_;
        }
        return {
            action: "",
            step: 0,
            stepCount: 5,
        };
    }

    get progressValue() {
        return (100 * this.localStepData.step) / this.localStepData.stepCount;
    }

    limit2decimals(percentage) {
        return mathFix(percentage, 2);
    }
}
